import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { recruitmentTheme, themeClasses } from '../styles/theme';
import { FileText, Save, Download, Eye, Plus, Upload, Users, X, Link } from 'lucide-react';
import { LocalStorageManager } from '../utils/localStorage';
import { useNotifications } from '../contexts/NotificationContext';
import { subscribeToCandidates } from '../services/candidateService';
import { addJob } from '../services/jobService';

interface JobData {
  gender: string[];
  qualifications: string[];
  locations: { country: string; city: string }[];
  salary: string;
  positions: string[];
  experience: string;
  accommodation: string;
  additionalDetails: string;
  files: File[];
  company: string;
  vacancies: number;
  linkedCandidates: string[];
  ageMaximumRange: string;
  nationality: string;
  dutyHours: string;
  workDays: string;
  overtime: string;
  transportProvision: string;
  contractPeriod: string;
}

const JobGeneratorPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification, notifyContentChange } = useNotifications();
  const [jobData, setJobData] = useState<JobData>({
    gender: [],
    qualifications: [],
    locations: [],
    salary: '',
    positions: [],
    experience: '',
    accommodation: '',
    additionalDetails: '',
    files: [],
    company: '',
    vacancies: 1,
    linkedCandidates: [],
    ageMaximumRange: '',
    nationality: '',
    dutyHours: '',
    workDays: '',
    overtime: '',
    transportProvision: '',
    contractPeriod: ''
  });

  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [customPosition, setCustomPosition] = useState('');
  const [plantType, setPlantType] = useState('');
  const [emergencyType, setEmergencyType] = useState('');
  const [showLinkCandidatesModal, setShowLinkCandidatesModal] = useState(false);
  const [candidates, setCandidates] = useState<any[]>([]);

  const genderOptions = ['Male', 'Female'];
  
  const qualificationOptions = [
    'Primary School',
    'Secondary School', 
    'High School',
    'Diploma',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'PhD/Doctorate'
  ];

  const gulfCountries = [
    'Saudi Arabia',
    'UAE', 
    'Kuwait',
    'Qatar',
    'Bahrain',
    'Oman'
  ];

  const citiesByCountry: { [key: string]: string[] } = {
    'Saudi Arabia': ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar'],
    'UAE': ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah'],
    'Kuwait': ['Kuwait City', 'Hawalli', 'Salmiya', 'Jahra', 'Ahmadi'],
    'Qatar': ['Doha', 'Al Rayyan', 'Al Wakrah', 'Al Khor', 'Dukhan'],
    'Bahrain': ['Manama', 'Riffa', 'Muharraq', 'Hamad Town', 'Isa Town'],
    'Oman': ['Muscat', 'Salalah', 'Nizwa', 'Sur', 'Sohar']
  };

  const positionOptions = [
    'Housemaids',
    'Waiters/Waitress', 
    'Baristas',
    'Cleaners',
    'Caregivers',
    'Drivers',
    'Truck Drivers',
    'Security Services',
    'Emergency Services',
    'Nurses',
    'Teachers',
    'Plant Technicians',
    'Erectors',
    'Fabrication Foreman',
    'Fabricator',
    'Fitter',
    'CNC Machine Operator',
    'Welder',
    'Forman - Steel factory',
    'Welder (stainless steel)',
    'Steel structure draftsman',
    'Helper',
    'Diesel Engine Mechanic',
    'Hydraulic Mechanic',
    'Tyre Man',
    'Auto Electrician',
    'Mechanic Helper',
    'Mechanic Foreman',
    'Car Denter',
    'Car Painter',
    'Helper (Painting)',
    'Central - AC Tech.',
    'Mobile crane driver'
  ];

  const emergencyServices = [
    'Firefighters',
    'Dispatchers', 
    'Crew Managers'
  ];

  const plantTypes = [
    'Gas Plants',
    'Power Plants',
    'Steel Plants', 
    'Water Treatment Plants'
  ];

  // Subscribe to Firebase candidates
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const setupCandidatesSubscription = async () => {
      try {
        console.log('🏭 JobGenerator: Setting up Firebase candidates subscription...');
        unsubscribe = await subscribeToCandidates((firebaseCandidates) => {
          console.log('🏭 JobGenerator: Received', firebaseCandidates.length, 'candidates from Firebase');
          setCandidates(firebaseCandidates);
        }, console.error);
      } catch (error) {
        console.error('🏭 JobGenerator: Failed to setup candidates subscription:', error);
      }
    };
    
    setupCandidatesSubscription();
    
    return () => {
      console.log('🏭 JobGenerator: Cleaning up Firebase candidates subscription...');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleCheckboxChange = (field: keyof JobData, value: string, checked: boolean) => {
    setJobData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field] as string[]), value]
        : (prev[field] as string[]).filter(item => item !== value)
    }));
  };

  const handleLocationAdd = () => {
    if (selectedCountry && selectedCity) {
      const newLocation = { country: selectedCountry, city: selectedCity };
      if (!jobData.locations.some(loc => loc.country === selectedCountry && loc.city === selectedCity)) {
        setJobData(prev => ({
          ...prev,
          locations: [...prev.locations, newLocation]
        }));
      }
      setSelectedCountry('');
      setSelectedCity('');
    }
  };

  const handlePositionAdd = (position: string) => {
    let finalPosition = position;
    
    if (position === 'Emergency Services' && emergencyType) {
      finalPosition = `${position} - ${emergencyType}`;
    } else if (position === 'Plant Technicians' && plantType) {
      finalPosition = `${position} - ${plantType}`;
    } else if (position === 'Other' && customPosition) {
      finalPosition = customPosition;
    }

    if (!jobData.positions.includes(finalPosition)) {
      setJobData(prev => ({
        ...prev,
        positions: [...prev.positions, finalPosition]
      }));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setJobData(prev => ({
      ...prev,
      files: [...prev.files, ...files]
    }));
  };

  const handleGenerate = async () => {
    try {
      // Parse salary to extract min and max values
      const salaryMatch = jobData.salary.match(/(\d+)(?:\s*-\s*(\d+))?/);
      const salaryMin = salaryMatch ? parseInt(salaryMatch[1]) : 0;
      const salaryMax = salaryMatch && salaryMatch[2] ? parseInt(salaryMatch[2]) : salaryMin;
      
      // Extract currency from salary string (default to SAR if not found)
      const currencyMatch = jobData.salary.match(/([A-Z]{3})/);
      const currency = currencyMatch ? currencyMatch[1] : 'SAR';
      
      // Format requirements as array
      const requirementsList = [
        ...(jobData.gender.length > 0 ? [`Gender: ${jobData.gender.join(', ')}`] : []),
        ...(jobData.qualifications.length > 0 ? [`Qualifications: ${jobData.qualifications.join(', ')}`] : []),
        ...(jobData.experience ? [`Experience: ${jobData.experience}`] : []),
        ...(jobData.accommodation ? [`Accommodation: ${jobData.accommodation}`] : [])
      ];
      
      // Format benefits as array
      const benefitsList = [
        ...(jobData.accommodation === 'Yes' ? ['Accommodation provided'] : []),
        ...(jobData.additionalDetails ? [jobData.additionalDetails] : ['Standard benefits package'])
      ];

      // Create job data compatible with Job interface
      const baseJobData = {
        title: jobData.positions.join(', ') || 'New Position',
        company: jobData.company || 'Naim Investments',
        location: jobData.locations.length > 0 
          ? jobData.locations.map(loc => `${loc.city}, ${loc.country}`).join('; ') 
          : 'Gulf Region',
        salary: jobData.salary || '0 SAR',
        type: 'full-time' as const,
        salaryRange: {
          min: salaryMin,
          max: salaryMax,
          currency: currency
        },
        status: 'Active' as const,
        requirements: requirementsList,
        benefits: benefitsList,
        description: `${jobData.experience ? `Experience: ${jobData.experience}. ` : ''}${jobData.additionalDetails || 'Exciting opportunity in the Gulf region.'}`,
        postedDate: new Date().toISOString().split('T')[0],
        // Extended fields from Job Generator
        positions: jobData.positions,
        gender: jobData.gender,
        vacancies: jobData.vacancies,
        uploads: jobData.files.map(file => file.name)
      };

      // Add optional fields only if they have values
      const optionalFields: any = {};
      
      if (jobData.locations.length > 0) {
        optionalFields.city = jobData.locations[0].city;
        optionalFields.country = jobData.locations[0].country;
        optionalFields.locations = jobData.locations;
      }
      
      if (jobData.experience) optionalFields.experience = jobData.experience;
      if (jobData.accommodation) optionalFields.accommodation = jobData.accommodation;
      if (jobData.additionalDetails) optionalFields.additionalDetails = jobData.additionalDetails;
      if (jobData.ageMaximumRange) optionalFields.ageMaximumRange = jobData.ageMaximumRange;
      if (jobData.nationality) optionalFields.nationality = jobData.nationality;
      if (jobData.dutyHours) optionalFields.dutyHours = jobData.dutyHours;
      if (jobData.workDays) optionalFields.workDays = jobData.workDays;
      if (jobData.overtime) optionalFields.overtime = jobData.overtime;
      if (jobData.transportProvision) optionalFields.transportProvision = jobData.transportProvision;
      if (jobData.contractPeriod) optionalFields.contractPeriod = jobData.contractPeriod;
      if (jobData.qualifications.length > 0) optionalFields.qualifications = jobData.qualifications;

      const newJobData = { ...baseJobData, ...optionalFields };

      // Save using Firebase to ensure it appears in JobsPage
      const newJobId = await addJob(newJobData);
      const newJob = { ...newJobData, id: newJobId };
      
      // Save linked candidates for this job
      if (jobData.linkedCandidates.length > 0) {
        const jobCandidatesKey = `job_candidates_${newJob.id}`;
        localStorage.setItem(jobCandidatesKey, JSON.stringify(jobData.linkedCandidates));
      }
      
      console.log('Job created with LocalStorageManager:', newJob.title);
      
      // Notify content change for new job
      notifyContentChange('Job Generator', 'added', 'Job', newJob.title);
      
      // Show success notification
      showNotification({
        type: 'success',
        title: 'Job Created Successfully',
        message: `${newJob.title} has been added to the jobs list and is now available across all pages`,
        priority: 'medium'
      });
      
      // Navigate to jobs page
      navigate('/jobs');
    } catch (error) {
      console.error('Failed to generate job:', error);
      showNotification({
        type: 'error',
        title: 'Job Creation Failed',
        message: 'Failed to generate job. Please check your data and try again.',
        priority: 'high'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${themeClasses.pageTitle} ${recruitmentTheme.animations.fadeIn}`}>
            Job Generator
          </h1>
          <p className="text-gray-600 mt-1">
            Create job postings quickly with ready-made sections and checkboxes
          </p>
        </div>
      </div>

      <div className={recruitmentTheme.components.card}>
        <div className="flex items-center space-x-2 mb-6">
          <FileText className="w-5 h-5 text-[#8b6914]" />
          <h2 className={themeClasses.sectionTitle}>Quick Job Builder</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Gender</label>
              <div className="space-y-2">
                {genderOptions.map(gender => (
                  <label key={gender} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={jobData.gender.includes(gender)}
                      onChange={(e) => handleCheckboxChange('gender', gender, e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">{gender}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Qualifications */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Qualifications</label>
              <div className="space-y-2">
                {qualificationOptions.map(qualification => (
                  <label key={qualification} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={jobData.qualifications.includes(qualification)}
                      onChange={(e) => handleCheckboxChange('qualifications', qualification, e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">{qualification}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Locations */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Locations</label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <select
                  value={selectedCountry}
                  onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    setSelectedCity('');
                  }}
                  className={recruitmentTheme.components.select}
                >
                  <option value="">Select Country</option>
                  {gulfCountries.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className={recruitmentTheme.components.select}
                  disabled={!selectedCountry}
                >
                  <option value="">Select City</option>
                  {selectedCountry && citiesByCountry[selectedCountry]?.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleLocationAdd}
                disabled={!selectedCountry || !selectedCity}
                className={`${recruitmentTheme.components.button.secondary} text-sm`}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Location
              </button>
              {jobData.locations.length > 0 && (
                <div className="mt-3 space-y-1">
                  {jobData.locations.map((location, index) => (
                    <span key={index} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2">
                      {location.city}, {location.country}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Salary */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Salary</label>
              <input
                type="text"
                value={jobData.salary}
                onChange={(e) => setJobData({...jobData, salary: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 2000 SAR/month"
              />
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Company Name</label>
              <input
                type="text"
                value={jobData.company}
                onChange={(e) => setJobData({...jobData, company: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., Saudi Arabia Elite Services"
              />
            </div>

            {/* Vacancies */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Number of Vacancies</label>
              <input
                type="number"
                min="1"
                value={jobData.vacancies}
                onChange={(e) => setJobData({...jobData, vacancies: parseInt(e.target.value) || 1})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 5"
              />
            </div>

            {/* Accommodation */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Accommodation</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="accommodation"
                    value="Yes"
                    checked={jobData.accommodation === 'Yes'}
                    onChange={(e) => setJobData({...jobData, accommodation: e.target.value})}
                    className="rounded"
                  />
                  <span className="text-sm">Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="accommodation"
                    value="No"
                    checked={jobData.accommodation === 'No'}
                    onChange={(e) => setJobData({...jobData, accommodation: e.target.value})}
                    className="rounded"
                  />
                  <span className="text-sm">No</span>
                </label>
              </div>
            </div>

            {/* Age Maximum Range */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Age Maximum Range</label>
              <input
                type="text"
                value={jobData.ageMaximumRange}
                onChange={(e) => setJobData({...jobData, ageMaximumRange: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 25-45 years"
              />
            </div>

            {/* Nationality */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Nationality</label>
              <input
                type="text"
                value={jobData.nationality}
                onChange={(e) => setJobData({...jobData, nationality: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., Any nationality, Filipino, Indian"
              />
            </div>

            {/* Duty Hours */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Duty Hours</label>
              <input
                type="text"
                value={jobData.dutyHours}
                onChange={(e) => setJobData({...jobData, dutyHours: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 8 hours/day, 9 AM - 6 PM"
              />
            </div>

            {/* Work Days */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Work Days</label>
              <input
                type="text"
                value={jobData.workDays}
                onChange={(e) => setJobData({...jobData, workDays: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 6 days/week, Monday-Friday"
              />
            </div>

            {/* Overtime */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Overtime</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="overtime"
                    value="Available"
                    checked={jobData.overtime === 'Available'}
                    onChange={(e) => setJobData({...jobData, overtime: e.target.value})}
                    className="rounded"
                  />
                  <span className="text-sm">Available</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="overtime"
                    value="Not Available"
                    checked={jobData.overtime === 'Not Available'}
                    onChange={(e) => setJobData({...jobData, overtime: e.target.value})}
                    className="rounded"
                  />
                  <span className="text-sm">Not Available</span>
                </label>
              </div>
            </div>

            {/* Transport Provision */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Transport Provision</label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="transportProvision"
                    value="Provided"
                    checked={jobData.transportProvision === 'Provided'}
                    onChange={(e) => setJobData({...jobData, transportProvision: e.target.value})}
                    className="rounded"
                  />
                  <span className="text-sm">Provided</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="transportProvision"
                    value="Not Provided"
                    checked={jobData.transportProvision === 'Not Provided'}
                    onChange={(e) => setJobData({...jobData, transportProvision: e.target.value})}
                    className="rounded"
                  />
                  <span className="text-sm">Not Provided</span>
                </label>
              </div>
            </div>

            {/* Contract Period */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Contract Period</label>
              <input
                type="text"
                value={jobData.contractPeriod}
                onChange={(e) => setJobData({...jobData, contractPeriod: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 2 years, 3 years renewable"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Positions */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-3">Positions</label>
              <div className="space-y-2">
                {positionOptions.map(position => (
                  <div key={position}>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            handlePositionAdd(position);
                          } else {
                            setJobData(prev => ({
                              ...prev,
                              positions: prev.positions.filter(p => !p.startsWith(position))
                            }));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{position}</span>
                    </label>
                    
                    {/* Emergency Services Dropdown */}
                    {position === 'Emergency Services' && (
                      <div className="ml-6 mt-2">
                        <select
                          value={emergencyType}
                          onChange={(e) => setEmergencyType(e.target.value)}
                          className={`${recruitmentTheme.components.select} text-sm`}
                        >
                          <option value="">Specify...</option>
                          {emergencyServices.map(service => (
                            <option key={service} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {/* Plant Technicians Dropdown */}
                    {position === 'Plant Technicians' && (
                      <div className="ml-6 mt-2">
                        <select
                          value={plantType}
                          onChange={(e) => setPlantType(e.target.value)}
                          className={`${recruitmentTheme.components.select} text-sm`}
                        >
                          <option value="">Specify...</option>
                          {plantTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Other Position */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked && customPosition) {
                          handlePositionAdd('Other');
                        } else {
                          setJobData(prev => ({
                            ...prev,
                            positions: prev.positions.filter(p => p !== customPosition)
                          }));
                        }
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">Other</span>
                  </label>
                  <div className="ml-6 mt-2">
                    <input
                      type="text"
                      value={customPosition}
                      onChange={(e) => setCustomPosition(e.target.value)}
                      className={`${recruitmentTheme.components.input} text-sm`}
                      placeholder="Specify position..."
                    />
                  </div>
                </div>
              </div>
              
              {jobData.positions.length > 0 && (
                <div className="mt-3 space-y-1">
                  {jobData.positions.map((position, index) => (
                    <span key={index} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs mr-2">
                      {position}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Experience */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Experience</label>
              <input
                type="text"
                value={jobData.experience}
                onChange={(e) => setJobData({...jobData, experience: e.target.value})}
                className={recruitmentTheme.components.input}
                placeholder="e.g., 2+ years in housekeeping"
              />
            </div>

            {/* Additional Details */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Additional Details</label>
              <textarea
                value={jobData.additionalDetails}
                onChange={(e) => setJobData({...jobData, additionalDetails: e.target.value})}
                className={`${recruitmentTheme.components.textarea} h-24`}
                placeholder="Add any additional requirements or information..."
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Upload Files</label>
              <div className="flex items-center space-x-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className={`${recruitmentTheme.components.button.secondary} cursor-pointer flex items-center space-x-2`}
                >
                  <Upload className="w-4 h-4" />
                  <span>Choose Files</span>
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Images, PDFs, documents, videos
              </p>
              {jobData.files.length > 0 && (
                <div className="mt-2">
                  {jobData.files.map((file, index) => (
                    <span key={index} className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs mr-2">
                      {file.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Link Candidates */}
            <div>
              <label className="block text-sm font-medium text-[#8b6914] mb-1">Link Candidates</label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setShowLinkCandidatesModal(true)}
                  className={`${recruitmentTheme.components.button.secondary} flex items-center space-x-2`}
                >
                  <Link className="w-4 h-4" />
                  <span>Select Candidates</span>
                </button>
                <span className="text-sm text-gray-600">
                  {jobData.linkedCandidates.length} selected
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Choose candidates to link to this job posting
              </p>
              {jobData.linkedCandidates.length > 0 && (
                <div className="mt-2">
                  {jobData.linkedCandidates.map((candidateId, index) => {
                    const candidate = candidates.find(c => c.id === candidateId);
                    return candidate ? (
                      <span key={index} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs mr-2">
                        {candidate.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="mt-8 text-center">
          <button 
            onClick={handleGenerate}
            className={`${recruitmentTheme.components.button.primary} px-8 py-3 text-lg flex items-center justify-center space-x-2 mx-auto`}
          >
            <FileText className="w-5 h-5" />
            <span>Generate Job</span>
          </button>
        </div>
      </div>

      {/* Link Candidates Modal */}
      {showLinkCandidatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#8b6914]">
                Select Candidates to Link
              </h2>
              <button 
                onClick={() => setShowLinkCandidatesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available Candidates */}
              <div>
                <h3 className="text-lg font-semibold text-[#8b6914] mb-4">Available Candidates</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {candidates
                    .filter(candidate => !jobData.linkedCandidates.includes(candidate.id))
                    .map(candidate => (
                      <div key={candidate.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{candidate.name}</p>
                          <p className="text-sm text-gray-600">{candidate.jobTitle || 'No job title'}</p>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                        <button
                          onClick={() => {
                            setJobData(prev => ({
                              ...prev,
                              linkedCandidates: [...prev.linkedCandidates, candidate.id]
                            }));
                          }}
                          className="flex items-center space-x-1 px-3 py-1 bg-[#8b6914] text-white rounded text-sm hover:bg-[#b8860b] transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Link</span>
                        </button>
                      </div>
                    ))}
                </div>
              </div>
              
              {/* Linked Candidates */}
              <div>
                <h3 className="text-lg font-semibold text-[#8b6914] mb-4">
                  Linked Candidates ({jobData.linkedCandidates.length})
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {jobData.linkedCandidates.map(candidateId => {
                    const candidate = candidates.find(c => c.id === candidateId);
                    if (!candidate) return null;
                    
                    return (
                      <div key={candidate.id} className="flex items-center justify-between p-3 border border-[#8b6914] bg-[#8b6914]/5 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{candidate.name}</p>
                          <p className="text-sm text-gray-600">{candidate.jobTitle || 'No job title'}</p>
                          <p className="text-sm text-gray-500">{candidate.email}</p>
                        </div>
                        <button
                          onClick={() => {
                            setJobData(prev => ({
                              ...prev,
                              linkedCandidates: prev.linkedCandidates.filter(id => id !== candidate.id)
                            }));
                          }}
                          className="flex items-center space-x-1 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                          <span>Remove</span>
                        </button>
                      </div>
                    );
                  })}
                  
                  {jobData.linkedCandidates.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      <p>No candidates linked yet.</p>
                      <p className="text-sm">Select candidates from the left to link them.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={() => setShowLinkCandidatesModal(false)}
                className={recruitmentTheme.components.button.secondary}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobGeneratorPage;